/**
 * CRM module request config — the CRM-audience scope + base URL for the
 * customer-management-api. The CRM SPA calls the api over HTTP; every call
 * mints a CRM-audience token via the scope-parameterized acquireToken(scopes)
 * (the auth_unification seam) — the customer-management-api validates
 * aud ∈ [crmClientId, api://crmClientId] (verify.ts), DIFFERENT from the experts
 * audience chat/admin use. NOT the parameterless acquireToken, NOT a literal.
 *
 * Resolved from build-time env so the scope + base URL track the deployment:
 *   - VITE_CRM_SCOPES        — explicit CRM-audience scope(s); preferred.
 *                              (was NEXT_PUBLIC_ENTRA_API_SCOPE in customer-management)
 *   - else the full configured scope union (single-registration dev default).
 *   - VITE_CRM_API_BASE_URL  — base for the CRM API. The lifted pages call paths
 *                              like '/api/customers'; the plan routes CRM under
 *                              /v1/crm/* via the edge. crmUrl() rewrites the
 *                              legacy '/api/*' prefix to `${base}/v1/crm/*` so the
 *                              27 call sites stay untouched.
 */
import { loadAuthConfig } from '@/auth/config';

function parseScopes(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The CRM-audience scopes acquireToken() should mint a token for. */
export function getCrmScopes(): string[] {
  const explicit = parseScopes(import.meta.env.VITE_CRM_SCOPES);
  if (explicit.length > 0) return explicit;
  return loadAuthConfig().scopes;
}

/**
 * Base for the CRM API. Defaults to same-origin '' (the edge fan-out serves
 * /v1/crm/* on app.andavolabs.com). Override with VITE_CRM_API_BASE_URL for a
 * distinct origin (e.g. local dev pointing at the customer-management-api).
 */
export function getCrmBaseUrl(): string {
  return import.meta.env.VITE_CRM_API_BASE_URL ?? '';
}

/**
 * Map a lifted CRM client path to the customer-management-api URL.
 *
 * The lifted pages pass legacy Next paths like '/api/customers',
 * '/api/notes?...'. The standalone backend serves these under '/v1/crm/*' (plan:
 * base path /v1/crm). We rewrite the leading '/api/' → '/v1/crm/' and prefix the
 * configured base — so the 27 call sites keep their original strings.
 */
export function crmUrl(path: string): string {
  const rewritten = path.replace(/^\/api\//, '/v1/crm/');
  const base = getCrmBaseUrl();
  if (!base) return rewritten;
  return `${base.replace(/\/$/, '')}${rewritten}`;
}
